import { useState } from 'react';
import { Plus, CheckCircle2, Clock, Trash2, CreditCard as Edit2, TrendingUp, X, Check } from 'lucide-react';
import { usePMEInvoices, PMEInvoiceInsert } from '../../hooks/usePMEInvoices';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getDaysUntil(dateStr: string) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

const PROBABILITY_OPTIONS = [
  { value: 100, label: '100% — Certain' },
  { value: 80, label: '80% — Probable' },
  { value: 50, label: '50% — Incertain' },
];

interface InvoiceFormData {
  client_name: string;
  amount_ht: string;
  invoice_date: string;
  expected_payment_date: string;
  status: 'pending' | 'paid';
  probability: number;
  notes: string;
}

const DEFAULT_FORM: InvoiceFormData = {
  client_name: '',
  amount_ht: '',
  invoice_date: new Date().toISOString().split('T')[0],
  expected_payment_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  status: 'pending',
  probability: 100,
  notes: '',
};

interface InvoiceModalProps {
  initial?: InvoiceFormData & { id?: string };
  onClose: () => void;
  onSave: (data: PMEInvoiceInsert) => Promise<void>;
  title: string;
}

function InvoiceModal({ initial, onClose, onSave, title }: InvoiceModalProps) {
  const [form, setForm] = useState<InvoiceFormData>(initial || DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(field: keyof InvoiceFormData, value: string | number) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.client_name.trim()) { setError('Le nom du client est requis'); return; }
    if (!form.amount_ht || isNaN(Number(form.amount_ht)) || Number(form.amount_ht) <= 0) {
      setError('Le montant doit être supérieur à 0'); return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        client_name: form.client_name.trim(),
        amount_ht: Number(form.amount_ht),
        invoice_date: form.invoice_date,
        expected_payment_date: form.expected_payment_date,
        status: form.status,
        probability: form.probability,
        notes: form.notes,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="Nom du client"
              value={form.client_name}
              onChange={e => set('client_name', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Montant HT (€) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="0"
                value={form.amount_ht}
                onChange={e => set('amount_ht', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Probabilité</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                value={form.probability}
                onChange={e => set('probability', Number(e.target.value))}
              >
                {PROBABILITY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de facturation</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={form.invoice_date}
                onChange={e => set('invoice_date', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date paiement estimée</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={form.expected_payment_date}
                onChange={e => set('expected_payment_date', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <div className="flex gap-2">
              {(['pending', 'paid'] as const).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => set('status', s)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                    form.status === s
                      ? s === 'paid' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {s === 'pending' ? 'En attente' : 'Payée'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
              rows={2}
              placeholder="Informations complémentaires..."
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
            />
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} fullWidth>Annuler</Button>
            <Button type="submit" loading={saving} fullWidth>Enregistrer</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PMEInvoicesPage() {
  const { invoices, totalPending, loading, addInvoice, updateInvoice, deleteInvoice, markAsPaid } = usePMEInvoices();
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<(InvoiceFormData & { id: string }) | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'paid'>('all');

  const filtered = invoices.filter(i => filter === 'all' ? true : i.status === filter);

  const totalAll = invoices.reduce((s, i) => s + i.amount_ht, 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount_ht, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showModal && (
        <InvoiceModal
          title="Nouvelle facture"
          onClose={() => setShowModal(false)}
          onSave={addInvoice}
        />
      )}
      {editItem && (
        <InvoiceModal
          title="Modifier la facture"
          initial={editItem}
          onClose={() => setEditItem(null)}
          onSave={data => updateInvoice(editItem.id, data)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Factures clients</h2>
          <p className="text-sm text-gray-500 mt-0.5">Suivez vos encaissements et relances</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus size={16} />
          Ajouter
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <TrendingUp size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total facturé</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(totalAll)}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
              <Clock size={18} className="text-orange-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">En attente (pondéré)</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(totalPending)}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <CheckCircle2 size={18} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Encaissé</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(totalPaid)}</p>
            </div>
          </div>
        </Card>
      </div>

      <Card padding="none">
        <div className="flex items-center gap-1 p-4 border-b border-gray-100">
          {(['all', 'pending', 'paid'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {f === 'all' ? 'Toutes' : f === 'pending' ? 'En attente' : 'Payées'}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-400">{filtered.length} facture{filtered.length > 1 ? 's' : ''}</span>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <TrendingUp size={40} className="text-gray-200 mb-3" />
            <p className="text-sm font-medium text-gray-500">Aucune facture</p>
            <p className="text-xs text-gray-400 mt-1">Cliquez sur "Ajouter" pour créer votre première facture</p>
          </div>
        ) : (
          <>
            <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-50">
              <div className="col-span-3">Client</div>
              <div className="col-span-2 text-right">Montant HT</div>
              <div className="col-span-2 text-right">Pondéré</div>
              <div className="col-span-2">Paiement estimé</div>
              <div className="col-span-1">Proba.</div>
              <div className="col-span-1">Statut</div>
              <div className="col-span-1"></div>
            </div>

            <div className="divide-y divide-gray-50">
              {filtered.map(invoice => {
                const days = getDaysUntil(invoice.expected_payment_date);
                const isOverdue = invoice.status === 'pending' && days < 0;
                const isUrgent = invoice.status === 'pending' && days >= 0 && days <= 7;

                return (
                  <div key={invoice.id} className={`grid grid-cols-12 gap-4 px-4 py-3.5 items-center hover:bg-gray-50 transition-colors ${isOverdue ? 'bg-red-50/40' : ''}`}>
                    <div className="col-span-12 sm:col-span-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-gray-600">
                            {invoice.client_name.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{invoice.client_name}</p>
                          <p className="text-xs text-gray-400 sm:hidden">{formatCurrency(invoice.amount_ht)} · {formatDate(invoice.expected_payment_date)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="hidden sm:block col-span-2 text-right">
                      <span className="text-sm font-semibold text-gray-900">{formatCurrency(invoice.amount_ht)}</span>
                    </div>

                    <div className="hidden sm:block col-span-2 text-right">
                      <span className="text-sm font-medium text-gray-600">
                        {invoice.probability < 100 ? formatCurrency(invoice.amount_ht * invoice.probability / 100) : '—'}
                      </span>
                    </div>

                    <div className="hidden sm:block col-span-2">
                      <p className="text-sm text-gray-700">{formatDate(invoice.expected_payment_date)}</p>
                      {isOverdue && (
                        <p className="text-xs text-red-600 font-medium">En retard de {Math.abs(days)}j</p>
                      )}
                      {isUrgent && (
                        <p className="text-xs text-orange-600 font-medium">Dans {days}j</p>
                      )}
                    </div>

                    <div className="hidden sm:block col-span-1">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        invoice.probability === 100 ? 'bg-emerald-50 text-emerald-700' :
                        invoice.probability === 80 ? 'bg-blue-50 text-blue-700' :
                        'bg-orange-50 text-orange-700'
                      }`}>
                        {invoice.probability}%
                      </span>
                    </div>

                    <div className="hidden sm:block col-span-1">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        invoice.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'
                      }`}>
                        {invoice.status === 'paid' ? 'Payée' : 'En attente'}
                      </span>
                    </div>

                    <div className="col-span-12 sm:col-span-1 flex items-center justify-end gap-1">
                      {invoice.status === 'pending' && (
                        <button
                          onClick={() => markAsPaid(invoice.id)}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Marquer comme payée"
                        >
                          <Check size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => setEditItem({
                          id: invoice.id,
                          client_name: invoice.client_name,
                          amount_ht: String(invoice.amount_ht),
                          invoice_date: invoice.invoice_date,
                          expected_payment_date: invoice.expected_payment_date,
                          status: invoice.status,
                          probability: invoice.probability,
                          notes: invoice.notes || '',
                        })}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(invoice.id)}
                        className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Supprimer la facture ?</h3>
            <p className="text-sm text-gray-600 mb-6">Cette action est irréversible.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmDelete(null)} fullWidth>Annuler</Button>
              <Button variant="danger" onClick={async () => { await deleteInvoice(confirmDelete); setConfirmDelete(null); }} fullWidth>Supprimer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
